import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import Button from '@rio-cloud/rio-uikit/Button';
import useClipboard from '@rio-cloud/rio-uikit/useClipboard';
import { useEffect, useRef } from 'react';
import { closableSuccessNotification } from '../../ClickableNotifications';

export interface FormInputFieldProperties {
    translationId: string;
    disabled: boolean;
    currentValue: string;
    hasError?: boolean;
    hasFeedback?: boolean;
    feedbackMsg?: React.ReactFragment;
    onValueChange?: (e: React.FormEvent<HTMLInputElement>) => void;
    dataAttribute?: string;
    withCopyButton: boolean;
    hasLengthValidation?: boolean;
    maxLength?: number;
    autoFocus?: boolean;
}

export const FormInputField = (props: FormInputFieldProperties) => {
    const clipboard = useClipboard();
    const copiedValueRef = useRef(props.currentValue);
    const intl = useIntl();
    const {
        disabled,
        translationId,
        currentValue,
        hasError,
        hasFeedback,
        feedbackMsg,
        onValueChange,
        dataAttribute,
        withCopyButton,
        hasLengthValidation,
        maxLength,
        autoFocus,
    } = props;

    useEffect(() => {
        if (clipboard.copied) {
            closableSuccessNotification(
                <FormattedMessage id={'tags.tagDetails.copyFeedback'} values={{ tagName: copiedValueRef.current }} />
            );
        }
    }, [clipboard.copied]);

    const handleCopyButtonClick = () => {
        copiedValueRef.current = currentValue;
        clipboard.copy(currentValue);
    };

    const formGroupClassNames = classNames('form-group', hasError && 'has-error', hasFeedback && 'has-feedback');
    const lengthValidationClassNames = classNames(
        hasError && 'text-color-danger',
        !hasError && 'text-color-highlight',
        'position-absolute',
        'right-10',
        'top-50pct',
        'transform',
        'translate-top-50',
        'text-italic',
        'z-index-3',
        'pointer-events-none'
    );

    return (
        <div className={formGroupClassNames}>
            <label>
                <FormattedMessage id={translationId} />
            </label>
            <div className='input-group'>
                <input
                    className='form-control'
                    type='text'
                    readOnly={disabled}
                    value={currentValue ? currentValue : ''}
                    onChange={onValueChange ? onValueChange : () => {}}
                    maxLength={maxLength ? maxLength : -1}
                    data-cy={dataAttribute && dataAttribute}
                    // biome-ignore lint/a11y/noAutofocus: suppress out of convenience during migration to biome
                    autoFocus={autoFocus ? autoFocus : false}
                />
                {hasLengthValidation && maxLength ? (
                    <div className={lengthValidationClassNames}>
                        {currentValue === '' ? '' : `${currentValue.length}/${maxLength}`}
                    </div>
                ) : (
                    <React.Fragment />
                )}
                {withCopyButton ? (
                    <div className='input-group-addon'>
                        <Button
                            bsStyle={Button.MUTED}
                            bsSize={Button.SM}
                            className='position-absolute top-1 right-1'
                            disabled={!currentValue}
                            iconOnly
                            iconName='rioglyph-duplicate'
                            onClick={handleCopyButtonClick}
                            data-cy={dataAttribute && `${dataAttribute}-copy-btn`}
                            autoTrackingKey={translationId}
                            aria-label={intl.formatMessage({
                                id: 'tags.tagDetails.copyValue',
                                defaultMessage: 'Copy value',
                            })}
                        />
                    </div>
                ) : (
                    <React.Fragment />
                )}
            </div>
            {hasFeedback && feedbackMsg && <span className={'help-block'}>{feedbackMsg}</span>}
        </div>
    );
};
